import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import apiClient from "../utils/apiClient";
import ProjectInfoService, {
  ProjectInfoFields,
} from "../services/ProjectInfoService";

/**
 * Custom hook for managing PV panel scenarios
 *
 * @param {string} projectId - Project ID from URL parameters
 * @returns {Object} State and functions for PV panel scenarios management
 */
const usePVPanelScenarios = (projectId) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  const token = localStorage.getItem("token");

  const apiRoot = process.env.REACT_APP_API_ROOT;
  const apiRootOperational = process.env.REACT_APP_API_ROOT_OPERATIONAL;

  const projectInfoService = new ProjectInfoService(apiRoot, token);

  /**
   * Fetch scenarios from the server
   */
  const fetchScenarios = async () => {
    if (!projectId) {
      setError("Project ID is missing");
      setLoading(false);
      return { success: false, error: "Project ID is missing" };
    }

    try {
      setLoading(true);
      setError(null);

      const currentInfo = await projectInfoService.getProjectInfo(projectId);

      let scenarioList = currentInfo?.pv_panel_data;
      if (typeof scenarioList === "string") {
        scenarioList = JSON.parse(scenarioList);
      }
      setScenarios(Array.isArray(scenarioList) ? scenarioList : []);
    } catch (err) {
      console.error("Error fetching scenarios:", err);
      setError("Failed to load PV panel scenarios. Please try again.");
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }

    return { success: true };
  };

  useEffect(() => {
    fetchScenarios();
  }, [projectId]);

  /**
   * Handle scenario selection
   *
   * @param {string} scenarioId - ID of the scenario to select/deselect
   */
  const handleSelectScenario = (scenarioId) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      } else {
        return [...prev, scenarioId];
      }
    });
  };

  /**
   * Handle selection of all scenarios
   *
   * @param {Array} newSelectedScenarios - Array of scenario IDs to select
   */
  const handleSelectAllScenarios = (newSelectedScenarios) => {
    setSelectedScenarios(newSelectedScenarios);
  };

  /**
   * Delete selected scenarios
   *
   * @returns {Object} Result of the operation
   */
  const deleteScenarios = async () => {
    if (selectedScenarios.length === 0) {
      return { success: false, message: "No scenarios selected" };
    }

    try {
      setIsProcessing(true);

      for (const scenario of selectedScenarios) {
        const response = await fetch(
          `${apiRootOperational}/api/pv-panels?project_id=${projectId}&scenario=${scenario}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to delete scenario");
        }
      }

      const currentInfo = await projectInfoService.getProjectInfo(projectId);
      let pvPanelsScenarios = projectInfoService.parseJsonField(
        currentInfo.pv_panel_data
      );
      pvPanelsScenarios = pvPanelsScenarios.filter(
        (s) => !selectedScenarios.includes(s)
      );

      await projectInfoService.updateProjectInfo(projectId, {
        [ProjectInfoFields.PV_PANEL_SCENARIO]:
          projectInfoService.stringifyJsonField(pvPanelsScenarios),
      });

      setScenarios(pvPanelsScenarios);
      setSelectedScenarios([]);

      return {
        success: true,
        message: `Selected scenarios deleted successfully.`,
      };
    } catch (err) {
      console.error("Error deleting scenarios:", err);
      return {
        success: false,
        message: err.message || "Failed to delete scenarios",
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Upload a new scenario
   *
   * @param {string} scenarioName - Name of the new scenario
   * @param {File} file - Excel file with panel data
   * @returns {Object} Result of the operation
   */
  const uploadScenario = async (scenarioName, file) => {
    if (!scenarioName || !file) {
      return {
        success: false,
        message: "Please enter a scenario name and select a file.",
      };
    }

    try {
      setIsProcessing(true);

      if (scenarios.includes(scenarioName)) {
        return {
          success: false,
          message: "A scenario with this name already exists.",
        };
      }

      const fileData = await file.arrayBuffer();
      let workbook;
      try {
        workbook = XLSX.read(fileData, {
          type: "array",
          cellDates: true,
          cellNF: true,
        });
      } catch (error) {
        return {
          success: false,
          message: "Failed to read Excel file. Please check the file format.",
        };
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        raw: true,
        defval: null,
      });

      if (!jsonData || jsonData.length === 0) {
        return {
          success: false,
          message: "No data found in the Excel file.",
        };
      }

      const panels = jsonData.map((row, index) => {
        let area = 1.0;
        try {
          const polygonPoints = JSON.parse(row["Polygon [m]"]);
        } catch (e) {
          console.warn(`Could not parse polygon for row ${index}`);
        }

        return {
          text: row["Name"]?.toString() || "",
          pv_panel_polygon: row["Polygon [m]"]?.toString() || "",
          annual_yield: parseFloat(row["Yield [kWh]"]) || 0,
          area: parseFloat(row["Yield [kWh/m2]"]) || 0,
        };
      });

      const invalidPanels = panels.filter(
        (panel) =>
          !panel.text || !panel.pv_panel_polygon || panel.annual_yield === 0
      );

      if (invalidPanels.length > 0) {
        console.log("Invalid panels found:", invalidPanels);
        return {
          success: false,
          message:
            "Some panels contain invalid or missing data. Please check the Excel file.",
        };
      }

      const requestData = {
        project_id: parseInt(projectId),
        zone: scenarioName,
        panels: panels,
      };

      const response = await fetch(`${apiRootOperational}/api/pv-panels/bulk`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload scenario");
      }

      const data = await response.json();

      await projectInfoService.updateListField(
        projectId,
        "pv_panel_data",
        scenarioName,
        { overwrite: false, unique: true }
      );

      setScenarios((prev) => [...new Set([...prev, scenarioName])]);

      return {
        success: true,
        message: `Scenario "${scenarioName}" uploaded successfully. ${
          data.count ? `${data.count} panels processed.` : ""
        }`,
      };
    } catch (err) {
      console.error("Error in scenario upload process:", err);
      return {
        success: false,
        message: err.message || "Error uploading scenario",
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    scenarios,
    loading,
    isProcessing,
    error,
    selectedScenarios,

    fetchScenarios,
    handleSelectScenario,
    handleSelectAllScenarios,
    deleteScenarios,
    uploadScenario,
  };
};

export default usePVPanelScenarios;
